import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { ENVIRONMENT } from '@hmax-order/frontend/environment';
import { UserStateModel } from '@hmax-order/frontend/state';
import { AcceptedLocale } from '@hmax-order/shared/enums';
import { Company } from '@hmax-order/shared/types';
import { CompanyForm } from '../../../interfaces/company-form.type';

@Injectable({ providedIn: 'root' })
export class CompanyService {
  private env = inject(ENVIRONMENT);
  private _http = inject(HttpClient);

  private _url = `${this.env.apiUrl}/companies`;
  private _urlMe = `${this.env.apiUrl}/me`;
  private _urlCompanies = `${this.env.apiUrl}/me/companies`;

  create(company: Company): Observable<Company[]> {
    return this._http.post<Company[]>(this._url, company);
  }

  update(id: string, company: Company): Observable<Company[]> {
    return this._http.patch<Company[]>(`${this._url}/${id}`, company);
  }

  remove(id: string): Observable<Company[]> {
    return this._http.delete<Company[]>(`${this._url}/${id}`);
  }

  getAll(): Observable<Company[]> {
    return this._http.get<Company[]>(this._url);
  }

  findById(id: string) {
    return this._http.get<Company>(`${this._url}/${id}`);
  }

  getWithExtraData() {
    return this._http.get<Company[]>(`${this._url}?demo=0&includePersonAndAddress=1&includeOrganization=1`);
  }

  getMeCompanies(isSuperUser: boolean): Observable<Company[]> {
    return this._http.get<Company[]>(this._urlCompanies).pipe(
      map((companies) => {
        if (!isSuperUser) {
          return companies;
        }

        return companies.map((company) => this.checkBadge(company));
      }),
    );
  }

  changeCurrentCompany(id: string): Observable<Company[]> {
    return this._http.get<Company[]>(`${this._urlMe}/${id}`);
  }

  patchCurrentCompany(currentCompanyId: string | null): Observable<{ accessToken: string; user: UserStateModel }> {
    return this._http.patch<{ accessToken: string; user: UserStateModel }>(this._urlMe, { currentCompanyId });
  }
  /**
   * Converter dados do formulário para company
   */
  toCompany(companyForm: CompanyForm): Company {
    const { name, document, organization, active, demo, sandbox, email } = companyForm;
    const company: Partial<Company> = {
      name,
      document,
      organizationId: organization?.value,
      active,
      demo,
      sandbox,
      email,
      locale: AcceptedLocale.ptBR,
    };

    return company as Company;
  }

  /**
   * Aplica badge em uma lista de empresas
   */
  applyBadge(companies: Company[]): Company[] {
    return companies.map((company) => this.checkBadge(company));
  }

  /**
   * Define o badge exibido para a empresa com base nos atributos
   * `demo`, `sandbox`, `hasActiveSubscription` e `hasSubscription`, apenas quando o usuário for super.
   * Empresa que não é demo/sandbox:
   * - com assinatura ativa => "Assinante";
   * - sem assinatura ativa, mas que já teve assinatura => "Ex-Assinante";
   * - que nunca teve assinatura => "Lead".
   */
  private checkBadge(company: Partial<Company>): Partial<Company> {
    const { sandbox = false, demo = false } = company;

    const badge = {
      demo: { label: 'Demo', color: '#cfe8ff', textColor: '#1d4ed8' },
      sandbox: { label: 'Sandbox', color: '#fecaca', textColor: '#991b1b' },
      subscriber: { label: 'Assinante', color: '#bbf7d0', textColor: '#166534' },
      exSubscriber: { label: 'Ex-Assinante', color: '#e5e7eb', textColor: '#4b5563' },
      lead: { label: 'Lead', color: '#e9d5ff', textColor: '#6b21a8' },
    };

    if (demo) {
      company.badge = badge.demo;
    } else if (sandbox) {
      company.badge = badge.sandbox;
    } else {
      company.badge = badge.lead;
    }

    return company;
  }
}
